import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useDestroyPopMutation } from '~/libs/gql/mutations/eat/destroyPopMutation.generated';
import { useUpsertPopMutation } from '~/libs/gql/mutations/eat/upsertPopMutation.generated';
import { useMenuItemUserPopQuery } from '~/libs/gql/queries/eat/menuItemUserPopQuery.generated';
import { PopPopType } from '~/libs/gql/types';
import { useEatSession } from '../useEatSession';
import { useSignInPath } from '../useSignInPath';

// Pop types that show the dish as popped
const POPPED_POP_TYPES: string[] = [PopPopType.LovedIt, PopPopType.LikedIt];
// Pop types that count toward popsCount
const POSITIVE_POP_TYPES: string[] = [...POPPED_POP_TYPES, PopPopType.WannaTry];

type UsePopArgs = {
  menuItemId: number
  popsCount: number
}

// Whether the signed-in diner has popped a dish (loved or liked it), and a toggle for it. Eat pages are cached at the
// edge, so the diner's pop loads in the browser. Signed-out diners are sent to sign in, which brings them back to the dish.
export const usePop = ({ menuItemId, popsCount }: UsePopArgs) => {
  const intl = useIntl();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const signInPath = useSignInPath();
  const { loading: sessionLoading, user } = useEatSession();
  const { data, loading: userPopLoading } = useMenuItemUserPopQuery({
    skip: !user,
    ssr: false,
    variables: { menuItemId },
  });
  const [upsertPop, { loading: upserting }] = useUpsertPopMutation();
  const [destroyPop, { loading: destroying }] = useDestroyPopMutation();
  const userPop = user ? data?.menuItem?.userPop : null;
  const isPopped = Boolean(userPop && POPPED_POP_TYPES.includes(userPop.popType));
  const wasCounted = Boolean(userPop && POSITIVE_POP_TYPES.includes(userPop.popType));
  const loading = upserting || destroying;

  const togglePop = useCallback(async () => {
    // Until the diner's pop loads, a tap can't tell popping from unpopping
    if (sessionLoading || userPopLoading || loading) return;
    if (!user) {
      history.push(signInPath);
      return;
    }

    try {
      if (isPopped) {
        await destroyPop({
          optimisticResponse: { destroyPop: { __typename: 'MenuItem', id: menuItemId, popsCount: Math.max(popsCount - 1, 0), userPop: null } },
          variables: { menuItemId },
        });
      } else {
        await upsertPop({
          optimisticResponse: {
            upsertPop: {
              __typename: 'MenuItem',
              id: menuItemId,
              popsCount: wasCounted ? popsCount : popsCount + 1,
              userPop: { __typename: 'Pop', id: userPop?.id ?? -1, popType: PopPopType.LovedIt },
            },
          },
          variables: { menuItemId, popType: PopPopType.LovedIt },
        });
      }
    } catch {
      enqueueSnackbar(intl.formatMessage({ defaultMessage: "We couldn't save that. Please try again." }), { variant: 'error' });
    }
  }, [destroyPop, enqueueSnackbar, history, intl, isPopped, loading, menuItemId, popsCount, sessionLoading, signInPath, upsertPop, user, userPop, userPopLoading, wasCounted]);

  return { isPopped, togglePop };
};
